import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Paperclip, Send, X } from "lucide-react";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, apiPostForm, downloadBlob, PortalApiError } from "../api/portal-client";
import { classifyPortalError, type PortalStatus } from "../hooks/usePortalResource";
import { phCapture } from "../lib/posthog";

/** Doit rester aligné sur les bornes serveur (portal-messages.routes.ts). */
const MESSAGE_MAX_CHARS = 2000;
const ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;
const ATTACHMENT_MIMES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

/** Le fil se rafraîchit tout seul : l'artisan répond pendant que la page est ouverte. */
const POLL_INTERVAL_MS = 30_000;

interface PortalMessage {
  id: string;
  direction: "client_vers_artisan" | "artisan_vers_client";
  body: string;
  createdAt: string;
  readAt: string | null;
  attachment: { name: string; mime: string; size: number } | null;
}

interface MessagesResponse {
  items: PortalMessage[];
  hasMore: boolean;
  nextBefore: string | null;
}

/**
 * Fusionne une page reçue avec le fil déjà affiché : dédoublonnage par `id` et
 * tri chronologique. Sans cela, le rafraîchissement automatique (qui relit
 * TOUJOURS la page la plus récente) écrasait les pages plus anciennes que le
 * client venait de remonter.
 */
function mergeMessages(previous: PortalMessage[], incoming: PortalMessage[]): PortalMessage[] {
  const byId = new Map(previous.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => {
    const d = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return d !== 0 ? d : a.id.localeCompare(b.id);
  });
}

function formatMessageDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} Mo`;
}

/**
 * Contrôle local du fichier joint. Le serveur refait exactement les mêmes
 * vérifications (et renifle en plus la signature binaire) : ce contrôle-ci
 * n'existe que pour éviter au client d'attendre un upload voué au rejet.
 */
function attachmentError(file: File): string | null {
  if (file.size > ATTACHMENT_MAX_BYTES) return "Fichier trop lourd : 2 Mo maximum.";
  if (!ATTACHMENT_MIMES.includes(file.type)) return "Format non accepté : JPEG, PNG, WebP ou PDF uniquement.";
  return null;
}

/**
 * Envoi multipart. W7-C — il passait par un `fetch` nu : seul appel authentifié
 * du portail hors du client API, il ne bénéficiait donc d'aucune de ses gardes.
 * Sur session expirée, l'écran répondait « Envoi impossible. Réessayez. » et
 * laissait le client réessayer indéfiniment avec un jeton mort. Même URL, même
 * corps qu'avant : seul le chemin d'appel change.
 */
async function postMessage(body: string, file: File | null): Promise<PortalMessage> {
  const form = new FormData();
  form.append("body", body);
  if (file) form.append("file", file);
  return apiPostForm<PortalMessage>("/me/messages", form);
}

function MessageBubble({ message }: { message: PortalMessage }) {
  const mine = message.direction === "client_vers_artisan";

  async function handleDownload() {
    await downloadBlob(`/me/messages/${message.id}/attachment`, message.attachment?.name ?? "piece-jointe");
  }

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] sm:max-w-[70%] rounded-xl2 px-4 py-3 border ${
          mine ? "bg-terracotta text-white border-terracotta" : "bg-card text-ink border-border"
        }`}
      >
        {message.body && <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>}
        {message.attachment && (
          <button
            type="button"
            onClick={handleDownload}
            className={`mt-2 flex items-center gap-2 text-xs font-medium underline underline-offset-2 ${
              mine ? "text-white/90 hover:text-white" : "text-terracotta hover:text-terracotta-dark"
            }`}
          >
            <Paperclip size={14} />
            <span className="truncate max-w-[200px]">{message.attachment.name}</span>
            <span className={mine ? "text-white/70" : "text-muted"}>({formatSize(message.attachment.size)})</span>
          </button>
        )}
        <p className={`mt-1.5 text-xs ${mine ? "text-white/70" : "text-muted"}`}>
          {formatMessageDate(message.createdAt)}
        </p>
      </div>
    </div>
  );
}

/**
 * Messages.
 *
 * W7-B — deux défauts de fond. (1) « Aucun message pour l'instant » s'affichait
 * aussi quand la lecture échouait : le client concluait que son fournisseur ne
 * lui avait jamais écrit. (2) L'API pagine à 50 messages et renvoie
 * `hasMore`/`nextBefore` — le portail les jetait : au-delà de 50, le début de la
 * conversation était INATTEIGNABLE, sans un mot. UN seul contrôle remonte le fil,
 * et le rafraîchissement automatique ne rejette plus les pages déjà chargées.
 */
export default function MessagesPage() {
  const [messages, setMessages] = useState<PortalMessage[]>([]);
  const [status, setStatus] = useState<PortalStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Pagination : curseur de la page la plus ANCIENNE déjà lue. Le
  // rafraîchissement (qui relit la page récente) n'y touche jamais.
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setStatus("loading");
    try {
      const data = await apiGet<MessagesResponse>("/me/messages");
      setMessages((prev) => mergeMessages(prev, data.items));
      if (!opts.silent) {
        setOlderCursor(data.nextBefore);
        setHasOlder(data.hasMore);
      }
      setStatus("ready");
      setError(null);
    } catch (e: unknown) {
      // Un échec de rafraîchissement silencieux ne doit pas effacer le fil déjà affiché.
      if (opts.silent) return;
      setStatus(classifyPortalError(e));
      setError(e instanceof PortalApiError ? e.message : null);
    }
  }, []);

  async function loadOlder() {
    if (!olderCursor || loadingOlder) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const data = await apiGet<MessagesResponse>(
        `/me/messages?before=${encodeURIComponent(olderCursor)}`,
      );
      setMessages((prev) => mergeMessages(prev, data.items));
      setOlderCursor(data.nextBefore);
      setHasOlder(data.hasMore);
    } catch (e: unknown) {
      setOlderError(
        classifyPortalError(e) === "offline"
          ? "Hors ligne : impossible de remonter le fil pour l'instant."
          : "Impossible de charger les messages plus anciens.",
      );
    } finally {
      setLoadingOlder(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => void load({ silent: true }), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Le fil se recale en bas sur un message RÉCENT, jamais quand le client vient
  // de remonter l'historique — sinon « voir les messages plus anciens » le
  // renvoyait aussitôt à la fin de la conversation.
  const dernierId = useMemo(() => messages.at(-1)?.id ?? null, [messages]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [dernierId]);

  function handlePickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      setFile(null);
      setFileError(null);
      return;
    }
    const problem = attachmentError(picked);
    if (problem) {
      setFile(null);
      setFileError(problem);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(picked);
    setFileError(null);
  }

  function clearFile() {
    setFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if ((!body && !file) || sending) return;

    setSending(true);
    setSendError(null);
    try {
      const created = await postMessage(body, file);
      // Un rafraîchissement lancé pendant l'envoi peut déjà l'avoir ramené :
      // sans ce filtre, la bulle apparaîtrait en double (clés React identiques).
      setMessages((prev) => [...prev.filter((m) => m.id !== created.id), created]);
      setDraft("");
      clearFile();
      phCapture("portal_cta_clic", { cta: "message_envoye" });
    } catch (e: unknown) {
      setSendError(e instanceof PortalApiError ? e.message : "Envoi impossible. Réessayez.");
    } finally {
      setSending(false);
    }
  }

  const remaining = MESSAGE_MAX_CHARS - draft.length;

  return (
    <main className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader title="Messages" subtitle="Échangez directement avec votre fournisseur." />

      <PortalState status={status} label="votre conversation" error={error} onRetry={() => void load()} />

      {status === "ready" && messages.length === 0 && (
        <EmptyState
          icon={MessageSquare}
          title="Aucun message pour l'instant"
          description="Écrivez le premier — votre fournisseur sera prévenu."
          action={{ label: "Écrire un message", onClick: () => composerRef.current?.focus() }}
        />
      )}

      {status === "ready" && messages.length > 0 && (
        <div className="bg-cream border border-border rounded-xl2 p-4 min-h-[280px] max-h-[55vh] overflow-y-auto space-y-3">
          {/* Pagination UNIQUE du fil, en tête : c'est le sens de lecture d'une
              conversation — le plus ancien est en haut. */}
          {hasOlder && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => void loadOlder()}
                disabled={loadingOlder}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-terracotta hover:bg-card disabled:opacity-50"
              >
                {loadingOlder ? "Chargement…" : "Voir les messages plus anciens"}
              </button>
            </div>
          )}
          {olderError && (
            <p role="alert" className="text-center text-xs text-danger">
              {olderError}
            </p>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      <form onSubmit={handleSend} className="mt-4">
        <textarea
            ref={composerRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MESSAGE_MAX_CHARS))}
            rows={3}
            placeholder="Votre message..."
            aria-label="Votre message"
            className="w-full rounded-xl2 border border-border bg-card px-3 py-2 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          />

          {file && (
            <div className="mt-2 flex items-center gap-2 text-sm text-ink">
              <Paperclip size={16} className="text-muted" />
              <span className="truncate max-w-[220px]">{file.name}</span>
              <span className="text-muted text-xs">({formatSize(file.size)})</span>
              <button type="button" onClick={clearFile} aria-label="Retirer la pièce jointe" className="text-muted hover:text-danger">
                <X size={16} />
              </button>
            </div>
          )}
          {fileError && <p className="mt-2 text-sm text-danger">{fileError}</p>}
          {sendError && <p className="mt-2 text-sm text-danger">{sendError}</p>}

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-terracotta cursor-pointer">
                <Paperclip size={16} />
                <span>Joindre un fichier</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACHMENT_MIMES.join(",")}
                  onChange={handlePickFile}
                  className="hidden"
                />
              </label>
              <span className="text-xs text-muted">{remaining} caractères restants</span>
            </div>
            <Button type="submit" disabled={sending || (!draft.trim() && !file)}>
              <Send size={16} />
              {sending ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        <p className="mt-2 text-xs text-muted">Pièce jointe : JPEG, PNG, WebP ou PDF, 2 Mo maximum.</p>
      </form>
    </main>
  );
}
